import {
  AloftArea,
  CraftsmanCostProps,
  MainPropertyType,
  getCraftsmanCost,
  isDefined,
  isUndefined,
  schAloftArea,
  schAloftPrice,
  schCraftsmanSFRQualityClasses,
  schManufacturedHomeFoundation,
  schPositiveInt,
  schTermsOfSale,
  syntaxFNMA,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { z } from 'zod';

import { ObjectiveCharacteristicsFormInput } from '../../../modules/analysis/schObjectiveCharacteristics.ts';
import { schIsoMonth } from '../../../modules/market/types.ts';
import { selectCostProps } from './costSelectors/selectCostProps.ts';
import { selectImprovementsEntities } from './propertyDataSelectors/improvementsSelectors.ts';
import { selectSiteEntities } from './propertyDataSelectors/siteSelectors.ts';
import { selectTransactionEntities } from './propertyDataSelectors/transactionSelectors.ts';

const schPositiveIntToForm = schPositiveInt.optional().transform((val) => val?.toString() ?? '');
const schPriceToForm = schAloftPrice
  .optional()
  .transform((val) => val?.toDollarLike().toString() ?? '');

const getSchAreaToForm = (defaultArea?: AloftArea) =>
  schAloftArea
    .optional()
    .default(defaultArea ?? AloftArea.zero)
    .transform((val) => val?.getUnitAmount().toString() ?? '');

const getSchAreaToFormWithUnit = (defaultArea?: AloftArea) =>
  schAloftArea
    .optional()
    .default(defaultArea ?? AloftArea.zero)
    .transform((val) => {
      if (val === undefined) return undefined;
      return {
        area: val.getUnitAmount(val.getPreferredUnit()).toString(),
        unit: val.getPreferredUnit(),
      };
    });

const getSchAreaAndQuality = (defaultArea?: AloftArea) =>
  z
    .object({
      area: getSchAreaToForm(defaultArea),
      quality: schPositiveIntToForm,
    })
    .or(z.undefined());

const getSchAttic = (defaultArea?: AloftArea) =>
  z
    .object({
      hasFeature: z.boolean(),
      area: getSchAreaToForm(defaultArea),
      quality: schPositiveIntToForm.optional(),
    })
    .or(z.undefined());

const getSchPorchPatioDeck = (defaultArea?: AloftArea) =>
  z
    .object({
      hasFeature: z.boolean(),
      type: z.enum(['covered', 'screen', 'uncovered']),
      area: getSchAreaToForm(defaultArea),
      quality: schPositiveIntToForm.optional(),
    })
    .or(z.undefined());

const getSchFeatureAreaAndQuality = (defaultArea?: AloftArea) =>
  z
    .object({
      hasFeature: z.boolean(),
      area: getSchAreaToForm(defaultArea),
      quality: schPositiveIntToForm,
    })
    .or(z.undefined());

const getSchFireplace = () =>
  z.object({
    count: schPositiveIntToForm,
    type: z.enum(['freestanding', 'zeroClearance', 'masonry']),
  });

const schAdu = z.object({
  area: getSchAreaToForm(),
  quality: z.coerce.string(),
  halfBaths: schPositiveIntToForm,
  fullBaths: schPositiveIntToForm,
});

const getSchNumberOfUnits = (propertyType: MainPropertyType, defaultUnits = 2) =>
  propertyType === 'multiFamily'
    ? schPositiveInt
        .optional()
        .default(defaultUnits)
        .transform((val) => val?.toString() ?? '')
    : z.undefined();

const getSchFoundation = (propertyType: MainPropertyType) =>
  propertyType === 'manufacturedHome' ? schManufacturedHomeFoundation : z.undefined();

const getSchema = (costProps: CraftsmanCostProps, propertyType: MainPropertyType) => {
  const calculator = getCraftsmanCost(costProps);

  const schParking = z
    .object({
      area: getSchAreaToForm(),
      spaces: schPositiveIntToForm.optional(),
      quality: schPositiveIntToForm.optional(),
    })
    .or(z.undefined());

  const schBasement = z
    .object({
      finishedArea: getSchAreaToForm(),
      totalArea: getSchAreaToForm(),
      fullBaths: schPositiveIntToForm,
      halfBaths: schPositiveIntToForm,
    })
    .or(z.undefined());

  return z.object({ id: z.string(), gla: getSchAreaToForm() }).merge(
    z
      .object({
        garage: schParking,
        carport: schParking,
        numberOfUnits: getSchNumberOfUnits(propertyType),
        foundation: getSchFoundation(propertyType),
        fullBaths: schPositiveIntToForm,
        halfBaths: schPositiveIntToForm,
        basement: schBasement,
        shed: getSchAreaAndQuality(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        workshop: getSchAreaAndQuality(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        barn: getSchAreaAndQuality(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        pool: getSchFeatureAreaAndQuality(calculator.constants.TYPICAL_POOL_SIZE_SQFT),
        spa: getSchFeatureAreaAndQuality(calculator.constants.TYPICAL_SPA_SIZE_SQFT),
        area: getSchAreaToFormWithUnit(),
        location: z.string(),
        view: z.string(),
        notes: z.string(),
        price: schPriceToForm,
        adu: schAdu,
        porch: getSchPorchPatioDeck(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        patio: getSchPorchPatioDeck(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        deck: getSchPorchPatioDeck(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        attic: getSchAttic(calculator.constants.ADD_STRUCT_DEFAULT_SQFT),
        fireplace: getSchFireplace(),
        manualCost: schPriceToForm,
        manualCostDisplayName: z.string(),
        age: schPositiveIntToForm,
        craftsmanQuality: schCraftsmanSFRQualityClasses,
        uadQuality: schPositiveIntToForm,
        uadCondition: schPositiveIntToForm,
        concessions: z.string(),
        termsOfSale: schTermsOfSale,
        status: syntaxFNMA.schUadListingStatus.optional(),
        saleDate: schIsoMonth.optional(),
        contractDate: schIsoMonth.optional(),
        withdrawalDate: schIsoMonth.optional(),
        expirationDate: schIsoMonth.optional(),
        daysOnMarket: schPositiveIntToForm,
        originalListPrice: schPriceToForm,
      })
      .partial(),
  );
};

export const selectObjectiveCharacteristicsForForm = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectSiteEntities,
    selectTransactionEntities,
    (_state, id: string) => id,
  ],
  (costProps, improvements, site, transaction, id): ObjectiveCharacteristicsFormInput => {
    const improvementsForTransaction = improvements[id];
    const siteForTransaction = site[id];
    const transactionForTransaction = transaction[id];

    if (
      isUndefined(improvementsForTransaction) ||
      isUndefined(siteForTransaction) ||
      isUndefined(transactionForTransaction)
    )
      return {
        id,
        gla: '',
        status: '',
        termsOfSale: '',
        saleDate: '',
        contractDate: '',
        expirationDate: '',
        withdrawalDate: '',
        craftsmanQuality: '',
      };

    const {
      gla,
      garage,
      carport,
      fullBaths,
      halfBaths,
      basement,
      shed,
      workshop,
      barn,
      pool,
      spa,
      adu,
      porch,
      patio,
      deck,
      attic,
      fireplace,
      manualCost,
      manualCostDisplayName,
      age,
      craftsmanQuality,
      uadQuality,
      uadCondition,
      propertyType,
    } = improvementsForTransaction;

    if (propertyType === undefined) {
      console.error('Property Type is undefined');
      return {
        id,
        gla: '',
        status: '',
        termsOfSale: '',
        saleDate: '',
        contractDate: '',
        expirationDate: '',
        withdrawalDate: '',
        craftsmanQuality: '',
      };
    }

    const { area, location, view, notes } = siteForTransaction;

    const {
      price,
      concessions,
      termsOfSale,
      status,
      contractDate,
      saleDate,
      expirationDate,
      withdrawalDate,
      daysOnMarket,
      originalListPrice,
    } = transactionForTransaction;

    const foundation =
      propertyType === 'manufacturedHome' ? improvementsForTransaction.foundation : undefined;
    const numberOfUnits =
      propertyType === 'multiFamily' ? improvementsForTransaction.numberOfUnits : undefined;

    const data = {
      id,
      gla,
      numberOfUnits,
      garage,
      carport,
      fullBaths,
      halfBaths,
      foundation,
      basement,
      shed,
      workshop,
      barn,
      pool,
      spa,
      area,
      location,
      view,
      notes,
      price,
      adu,
      porch,
      patio,
      deck,
      attic,
      fireplace,
      manualCost,
      manualCostDisplayName,
      age,
      craftsmanQuality,
      uadQuality,
      uadCondition,
      concessions,
      termsOfSale,
      status,
      contractDate,
      saleDate,
      expirationDate,
      withdrawalDate,
      daysOnMarket,
      originalListPrice,
    };

    const schStateToObjectiveCharacteristics = getSchema(costProps, propertyType);
    const formData = schStateToObjectiveCharacteristics.safeParse(data);

    if (formData.success) {
      return {
        ...formData.data,
        garage: prepareAdditionalQualityStructure(formData.data.garage, craftsmanQuality),
        carport: prepareAdditionalQualityStructure(formData.data.carport, craftsmanQuality),
        attic: prepareAdditionalQualityStructure<'attic'>(formData.data.attic, craftsmanQuality),
        porch: prepareAdditionalQualityStructure<'porch'>(formData.data.porch, craftsmanQuality),
        patio: prepareAdditionalQualityStructure<'patio'>(formData.data.patio, craftsmanQuality),
        deck: prepareAdditionalQualityStructure<'deck'>(formData.data.deck, craftsmanQuality),
        status: formData.data.status ?? '',
        termsOfSale: formData.data.termsOfSale ?? '',
        saleDate: formData.data.saleDate ?? '',
        contractDate: formData.data.contractDate ?? '',
        expirationDate: formData.data.expirationDate ?? '',
        withdrawalDate: formData.data.withdrawalDate ?? '',
        craftsmanQuality: formData.data.craftsmanQuality?.toString() ?? '',
      };
    }

    console.error(formData.error.issues);
    return {
      id,
      gla: '',
      status: '',
      termsOfSale: '',
      saleDate: '',
      contractDate: '',
      expirationDate: '',
      withdrawalDate: '',
      craftsmanQuality: '',
    };
  },
);

/**
 * These structures might not have the quality field set, so we need to set it to the
 * main structure's cost quality (i.e. Craftsman Quality) because that reflects what the
 * calculator will default to when that quality field is not available.
 */
const prepareAdditionalQualityStructure = <
  T extends 'attic' | 'garage' | 'carport' | 'porch' | 'patio' | 'deck',
>(
  struct: ObjectiveCharacteristicsFormInput[T],
  craftsmanQuality?: number,
): ObjectiveCharacteristicsFormInput[T] => {
  if (struct === undefined) return undefined;

  const roundedCraftsmanQuality = isDefined(craftsmanQuality)
    ? Math.round(craftsmanQuality)
    : undefined;

  return {
    ...struct,
    quality: struct.quality ?? roundedCraftsmanQuality?.toString(),
  };
};
